import { ChangeEvent, KeyboardEvent, useRef, useState } from "react";
import { Box, Button, ButtonBase, Skeleton } from "@mui/material";
import moment from "moment";
import strings from "global/constants/StringConstants";
import { isUnlimitedLimit } from "helpers/methods";
import { formatMoney } from "helpers/perSeatHelpers";
import { fillCopy } from "helpers/smsCreditHelpers";
import useSeatPreview from "hooks/use-seat-preview";
import {
  costLabelSx,
  costNoteSx,
  costRowSx,
  costStackSx,
  costValueSx,
  hintLinkSx,
  hintSx,
  orderBarSx,
  orderButtonSx,
  orderLineSx,
  orderNoteSx,
  orderTotalSx,
  panelHeadingSx,
  panelSx,
  statCellSx,
  statLabelSx,
  statRowSx,
  statValueSx,
  stepperButtonSx,
  stepperHintSx,
  stepperInputSx,
  stepperLabelSx,
  stepperRowSx,
  stepperSx,
} from "./ManageSeatsPanel.styles";

const copy = strings.manageSeats;

/**
 * Upper bound for a plan that states no seat cap. The backend clamps to the
 * plan's own cap regardless, so this only stops the stepper running away —
 * far past any real team, and small enough that seats × price stays exact.
 */
const UNCAPPED_PLAN_CEILING = 999999;

/**
 * How far each key moves the count. ARIA's spinbutton contract is that the
 * value answers the arrow and page keys, not only typing — a reader that
 * announces the role promises this, so the field has to honour it.
 */
const KEY_STEPS: Record<string, number> = {
  ArrowUp: 1,
  ArrowDown: -1,
  PageUp: 10,
  PageDown: -10,
};

const formatRenewalDate = (value?: string) => {
  if (!value) return "";
  const parsed = moment(value, "YYYY-MM-DD");
  return parsed.isValid() ? parsed.format("D MMM YYYY") : value;
};

const seatUnit = (count: number) =>
  count === 1 ? copy.seatUnitOne : copy.seatUnitMany;

interface Props {
  /** Seats the account has paid for today. */
  purchased: number;
  /** Seats currently filled by a billable user. */
  used: number;
  /** The plan's seat cap; beyond it the customer has to upgrade. */
  maxSeats: number;
  /**
   * Count to open on, when the panel was reached from somewhere that already
   * knows the answer — "buy a seat" after an add was refused arrives asking
   * for `purchased + 1`, and making the customer press `+` to reach the number
   * they just asked for is a step for nothing. Defaults to `purchased`.
   */
  initialSeats?: number;
  /** Per-seat price of the current plan, for the renewal estimate. */
  unitPrice: number;
  currency?: string;
  /**
   * The subscription's next-billing date, from `BillingSummary` — used only as
   * a fallback for the brief window before the seat preview (which quotes its
   * own, now-correct `nextRenewalDate` for the chosen count) has resolved.
   * Omitted and the preview hasn't loaded yet, the "then on {date}" line drops
   * the date rather than guess.
   */
  nextRenewalDate?: string;
  isSubmitting?: boolean;
  /** Receives the new purchased seat count. */
  onConfirm: (seats: number) => void;
  /**
   * Reaching the plan's cap. When given, the at-cap hint becomes a link that
   * hands off to it; omitted, the hint stays plain text.
   */
  onUpgrade?: () => void;
}

/**
 * "Manage seats" — the only in-plan action that moves money under the
 * buy-upfront model. Buying seats charges the prorated difference straight
 * away; releasing them credits the unused part to the account balance. Adding
 * or removing *users* never comes through here.
 *
 * Deliberately **not** an `AppModal`, and no longer one: this expands in place
 * inside the plan card the way the SMS credits buy panel does, and the header
 * button toggles it. §11.1 governs overlays, and this isn't one — which is
 * also why the panel has no Cancel of its own.
 *
 * The panel is mounted only while open, so every open starts from the seats
 * the account currently owns.
 *
 * Going below the seats in use is allowed: the API answers `409` with the
 * people to choose between, and the caller shows the keep-picker.
 *
 * The preview (`GET /billing/fetch/seat/preview/{count}`) takes the target
 * total seat count and is exact both ways — an increase prices the added
 * seats' remaining time, a decrease prices the released seats' unused time as
 * a credit — so every figure below comes straight from it, debounced as the
 * stepper moves rather than fetched per click.
 */
const ManageSeatsPanel = ({
  purchased,
  used,
  maxSeats,
  initialSeats,
  unitPrice,
  currency,
  nextRenewalDate,
  isSubmitting = false,
  onConfirm,
  onUpgrade,
}: Props) => {
  // Only the plan's cap bounds the top; an uncapped plan has no ceiling worth
  // enforcing here, and the floor is one seat rather than the active users.
  const floor = 1;
  const hasCap = maxSeats > 0 && !isUnlimitedLimit(maxSeats);
  /**
   * The cap can sit below what the account already owns — a plan changed
   * underneath an open session, or a grandfathered count the backend never
   * clamped. Admit `purchased` so the panel opens on the true number instead
   * of one that contradicts its own "up to N" copy. Clamping the seed down
   * instead would arm a "Release seats" confirm the moment it opens; the
   * stepper still refuses to climb any higher than where it starts.
   */
  const ceiling = hasCap
    ? Math.max(floor, maxSeats, purchased)
    : UNCAPPED_PLAN_CEILING;

  /**
   * Seeded once, on mount. `purchased` can change underneath an open panel — a
   * background refresh of the plan limit — and re-seeding on that would wipe a
   * seat count the customer is halfway through typing. A requested count still
   * answers to the plan's cap: asking for one more seat than the plan allows
   * opens on the cap, not above it.
   */
  const seed = Math.min(
    ceiling,
    Math.max(floor, initialSeats && initialSeats > 0 ? initialSeats : purchased),
  );

  const [seats, setSeats] = useState<number>(seed);
  /**
   * The typed count is kept as text while editing so the field can be cleared
   * mid-edit; the floor and cap are applied when the edit finishes.
   */
  const [draft, setDraft] = useState<string>(String(seed));

  const applySeats = (value: number) => {
    setSeats(value);
    setDraft(String(value));
  };

  /**
   * The count a given draft resolves to, inside the plan's bounds.
   *
   * A cleared field means "unchanged", not "one seat" — the panel is still
   * pricing the last committed count, and confirming has to send that same
   * number rather than silently dropping the customer to the floor.
   */
  const resolveSeats = (value: string) => {
    if (!value.trim()) return seats;
    const parsed = Number(value);
    const wanted = Number.isFinite(parsed) && parsed > 0 ? parsed : floor;
    return Math.min(ceiling, Math.max(floor, wanted));
  };

  const handleSeatInput = (event: ChangeEvent<HTMLInputElement>) => {
    // Six digits is past any real seat count, including an uncapped plan's
    // sentinel — wide enough not to be an arbitrary ceiling of its own.
    const digits = event.target.value.replace(/\D/g, "").slice(0, 6);
    // Same cap the "+" button refuses to cross — typing past it must be just
    // as impossible as clicking past it, not merely reset once the field
    // blurs. The floor isn't enforced here: a low or empty value mid-edit is
    // still a real editing state (see `resolveSeats`), just not an out-of-
    // bounds one the way exceeding the cap is.
    const displayDigits =
      digits && Number(digits) > ceiling ? String(ceiling) : digits;
    setDraft(displayDigits);
    // The order bar and the confirm label read `seats`, so hold it inside the
    // plan's bounds as it's typed: an extra digit would otherwise show "Buy
    // 149 seats", and a lone "0" would show every seat being released, until
    // blur corrected either. The field keeps the raw text meanwhile.
    if (displayDigits) setSeats(resolveSeats(displayDigits));
  };

  const commitSeatInput = () => applySeats(resolveSeats(draft));

  const seatInputRef = useRef<HTMLInputElement | null>(null);

  /**
   * Stepping onto a bound disables the very button that was just pressed, and
   * a disabled control drops focus to `<body>` — stranding a keyboard user
   * mid-interaction. Hand focus to the field, the one control in the group no
   * bound ever disables. Only when the button really held focus, so a mouse
   * click doesn't yank the caret into the input.
   */
  const step = (delta: number, source?: HTMLElement | null) => {
    const next = Math.min(ceiling, Math.max(floor, seats + delta));
    applySeats(next);
    const willDisable = delta < 0 ? next <= floor : next >= ceiling;
    if (willDisable && source && document.activeElement === source) {
      seatInputRef.current?.focus();
    }
  };

  const added = seats - purchased;
  const {
    preview,
    isLoading,
    hasFailed: previewFailed,
  } = useSeatPreview(added !== 0, seats);

  const previewCurrency = preview?.currency ?? currency;

  /**
   * What the renewal becomes at the chosen count. The preview is priced for
   * `seats` directly, so its `nextRenewalAmount` is exact for whatever the
   * stepper landed on — increase or decrease, any size. Only falls back to the
   * catalog rate for the brief window before the debounced preview has
   * resolved (or if it fails).
   */
  const renewalTotal = preview ? preview.nextRenewalAmount : seats * unitPrice;
  const renewalDateLabel = preview?.nextRenewalDate
    ? formatRenewalDate(preview.nextRenewalDate)
    : nextRenewalDate
      ? formatRenewalDate(nextRenewalDate)
      : "";

  /** A release is settled as account credit, so nothing is charged today. */
  const isCredit = Boolean(preview && preview.amountDueNow < 0);
  const chargeAmount = preview && !isCredit ? preview.amountDueNow : 0;
  // `??`, not `||`: a fully-discounted seat has a legitimate `total` of 0, and
  // falling back to `amountDueNow` there would quote a cost that isn't really
  // there.
  const grossAmount = preview ? (preview.total ?? preview.amountDueNow) : 0;
  const creditApplied = isCredit ? 0 : Math.max(0, grossAmount - chargeAmount);

  /**
   * A failed preview does **not** block the change — the real charge is
   * confirmed server-side, and a declined card comes back as a `402` on the
   * seat change itself. The panel says the amount will be prorated instead.
   */
  const canConfirm = added !== 0 && !isLoading && !isSubmitting;

  const renewalNote = renewalDateLabel
    ? fillCopy(copy.orderNoteRenewalDated, {
        amount: formatMoney(renewalTotal, previewCurrency),
        date: renewalDateLabel,
      })
    : fillCopy(copy.orderNoteRenewal, {
        amount: formatMoney(renewalTotal, previewCurrency),
      });

  const getOrderLine = () => {
    if (added === 0) return copy.orderLineUnchanged;
    const count = Math.abs(added);
    return fillCopy(
      added > 0 ? copy.orderLineAdded : copy.orderLineReleased,
      { count, unit: seatUnit(count) },
    );
  };

  const getOrderNote = () => {
    if (added === 0) return copy.orderNoteUnchanged;
    // Named for what it is: a release never charges, so the figure beside it
    // must not read as one.
    if (added < 0) return `${copy.orderNoteReleaseLead} · ${renewalNote}`;
    return renewalNote;
  };

  const getConfirmLabel = () => {
    if (added === 0) return copy.saveLabel;
    const count = Math.abs(added);
    if (added > 0) {
      return isSubmitting
        ? copy.buyingLabel
        : fillCopy(copy.buyLabel, { count, unit: seatUnit(count) });
    }
    return isSubmitting
      ? copy.releasingLabel
      : fillCopy(copy.releaseLabel, { count, unit: seatUnit(count) });
  };

  /**
   * The itemisation above the bar. The charged-today figure lives in the bar
   * itself, so this only carries what that figure doesn't explain: the gross
   * seat cost when account credit covers part of it, the tax baked into it,
   * and the message when the preview couldn't be read at all.
   */
  const getCostLines = () => {
    if (added === 0 || isLoading) return null;

    if (previewFailed || !preview) {
      return (
        <Box sx={costStackSx}>
          <Box component="span" sx={costNoteSx}>
            {added > 0 ? copy.previewFailedBuy : copy.previewFailedRelease}
          </Box>
        </Box>
      );
    }

    if (creditApplied <= 0 && preview.tax <= 0) return null;

    return (
      <Box sx={costStackSx}>
        {creditApplied > 0 && (
          <>
            <Box sx={costRowSx}>
              <Box component="span" sx={costLabelSx}>
                {copy.seatCostLabel}
              </Box>
              <Box component="span" sx={costValueSx()}>
                {formatMoney(grossAmount, previewCurrency)}
              </Box>
            </Box>
            <Box sx={costRowSx}>
              <Box component="span" sx={costLabelSx}>
                {copy.creditAppliedLabel}
              </Box>
              <Box component="span" sx={costValueSx(true)}>
                {`-${formatMoney(creditApplied, previewCurrency)}`}
              </Box>
            </Box>
          </>
        )}
        {creditApplied > 0 && chargeAmount === 0 && (
          <Box component="span" sx={costNoteSx}>
            {copy.coveredByCredit}
          </Box>
        )}
        {preview.tax > 0 && (
          <Box component="span" sx={costNoteSx}>
            {fillCopy(copy.taxNote, {
              amount: formatMoney(preview.tax, previewCurrency),
            })}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={panelSx}>
      <Box sx={panelHeadingSx}>{copy.panelHeading}</Box>

      {/* Three numbers, three labels — "you have" and "in use" read as one
          figure repeated when they're buried in a sentence. */}
      <Box sx={statRowSx}>
        <Box sx={statCellSx}>
          <Box component="span" sx={statLabelSx}>
            {copy.statYouHave}
          </Box>
          <Box component="span" sx={statValueSx}>
            {purchased}
          </Box>
        </Box>
        <Box sx={statCellSx}>
          <Box component="span" sx={statLabelSx}>
            {copy.statInUse}
          </Box>
          <Box component="span" sx={statValueSx}>
            {used}
          </Box>
        </Box>
        <Box sx={statCellSx}>
          <Box component="span" sx={statLabelSx}>
            {copy.statPlanLimit}
          </Box>
          <Box component="span" sx={statValueSx}>
            {hasCap ? maxSeats : copy.noLimitValue}
          </Box>
        </Box>
      </Box>

      <Box sx={stepperRowSx}>
        <Box component="span" sx={stepperLabelSx}>
          {copy.seatsLabel}
        </Box>
        {/* The input carries the accessible name; naming the group as well
            makes screen readers announce it twice. */}
        <Box sx={stepperSx}>
          <ButtonBase
            id="manage-seats-decrement"
            aria-label="Remove a seat"
            disableRipple
            disabled={seats <= floor || isSubmitting}
            sx={stepperButtonSx}
            onClick={(event) => step(-1, event.currentTarget)}
          >
            −
          </ButtonBase>
          <Box
            component="input"
            id="manage-seats-input"
            type="text"
            inputMode="numeric"
            // The standard stepper pattern: the bounds and the current value
            // are announced, not just the field's name.
            role="spinbutton"
            aria-label="Number of seats"
            aria-valuenow={seats}
            // `seats` is clamped as the user types (so the order bar never
            // shows a bogus figure), which leaves it disagreeing with the text
            // in the field mid-edit. Announce the literal text while they
            // differ, so what is read matches what is displayed; once the value
            // commits the two converge and this drops away.
            aria-valuetext={
              draft !== "" && Number(draft) !== seats ? draft : undefined
            }
            aria-valuemin={floor}
            aria-valuemax={hasCap ? ceiling : undefined}
            ref={seatInputRef}
            value={draft}
            onChange={handleSeatInput}
            onBlur={commitSeatInput}
            onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
              if (event.key === "Enter") {
                event.currentTarget.blur();
                return;
              }
              // End means "the most this plan allows", so it only means
              // anything while there is a cap — on an uncapped plan it would
              // jump to the runaway sentinel.
              if (event.key === "Home" || (event.key === "End" && hasCap)) {
                event.preventDefault();
                applySeats(event.key === "Home" ? floor : ceiling);
                return;
              }
              const delta = KEY_STEPS[event.key];
              if (delta === undefined) return;
              // Otherwise the caret jumps to either end of the text as the
              // arrow keys move the value.
              event.preventDefault();
              step(delta);
            }}
            disabled={isSubmitting}
            sx={stepperInputSx}
          />
          <ButtonBase
            id="manage-seats-increment"
            aria-label="Add a seat"
            disableRipple
            disabled={seats >= ceiling || isSubmitting}
            sx={stepperButtonSx}
            onClick={(event) => step(1, event.currentTarget)}
          >
            +
          </ButtonBase>
        </Box>
        <Box component="span" sx={stepperHintSx}>
          {hasCap
            ? fillCopy(copy.planLimitHint, { max: maxSeats })
            : copy.noLimitHint}
        </Box>
      </Box>

      {/* Going below the seats in use is allowed, but it deactivates people —
          say so before they confirm rather than after. The hints and the cost
          both change as the stepper moves, and the cost is the consequence, so
          announce them rather than leaving a screen-reader user stepping
          blind. */}
      <Box aria-live="polite" aria-atomic="true">
        {added === 0 && (
          <Box component="span" sx={hintSx}>
            {copy.unchanged}
          </Box>
        )}
        {seats < used && (
          <Box component="span" sx={hintSx}>
            {fillCopy(copy.belowInUse, { used, seats })}
          </Box>
        )}
        {hasCap && seats >= ceiling && seats >= used && (
          <Box component="span" sx={hintSx}>
            {onUpgrade ? (
              <>
                {`${copy.atCapLead} `}
                <Box
                  component="button"
                  type="button"
                  sx={hintLinkSx}
                  onClick={onUpgrade}
                >
                  {copy.atCapAction}
                </Box>
                .
              </>
            ) : (
              copy.atCapPlain
            )}
          </Box>
        )}
        {getCostLines()}
      </Box>

      <Box sx={orderBarSx}>
        <Box component="span" sx={orderLineSx}>
          {getOrderLine()}
        </Box>
        <Box component="span" sx={orderNoteSx}>
          {getOrderNote()}
        </Box>
        {isLoading ? (
          <Skeleton variant="text" width={80} sx={orderTotalSx} />
        ) : (
          <Box component="span" sx={orderTotalSx}>
            {added === 0 || previewFailed || !preview
              ? copy.noTotal
              : formatMoney(chargeAmount, previewCurrency)}
          </Box>
        )}
        <Button
          id="manage-seats-confirm"
          variant="contained"
          disableRipple
          // Resolved from the field itself rather than from state, so the
          // confirmed number never depends on blur having landed first.
          onClick={() => onConfirm(resolveSeats(draft))}
          disabled={!canConfirm}
          sx={orderButtonSx(canConfirm)}
        >
          {getConfirmLabel()}
        </Button>
      </Box>
    </Box>
  );
};

export default ManageSeatsPanel;
